import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Lock, User, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff, Crown, ArrowRight } from "lucide-react";
import { LiventyLogoMark, LiventyWordmark } from "../components/common/LiventyLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ============================================================================
 *  VipRedeemPage — /invite/:code
 *  Fluxo híbrido: admin emite código → user abre link → email pré-preenchido
 *  → user define nome + senha → conta criada/atualizada → PRO liberado → redireciona
 * ========================================================================= */
export default function VipRedeemPage() {
  const { code } = useParams();
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [grant, setGrant] = useState(null);
  const [error, setError] = useState("");

  // form fields
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 1️⃣ Valida o código ao abrir a página
  useEffect(() => {
    if (!code) {
      setError("Código VIP inválido");
      setValidating(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/vip-access/${code}/validate`);
        setGrant(res.data);
        if (res.data?.name) setName(res.data.name);
      } catch (err) {
        const msg = err.response?.data?.detail || "Código VIP inválido ou já resgatado.";
        setError(msg);
      } finally {
        setValidating(false);
      }
    })();
  }, [code]);

  // 2️⃣ Resgata: cria conta (ou atualiza) e libera PRO
  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Digite seu nome completo");
      return;
    }
    if (!password || password.length < 6) {
      toast.error("Senha precisa ter no mínimo 6 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/vip-access/redeem`, {
        code,
        name: name.trim(),
        password,
      });
      // grava token + user no localStorage (mesma key usada no resto do app)
      const token = res.data.access_token;
      const user = res.data.user;
      if (token) {
        localStorage.setItem("token", token);
        // setamos axios header sincronamente para evitar race condition
        // (mesma técnica do AuthProvider top-level)
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
        // marca tour já visto para NÃO disparar onboarding tour no dashboard
        localStorage.setItem("nosly_tour_completed", "true");
      }
      toast.success("🎉 Bem-vindo ao Liventy PRO!");
      // 🚀 BYPASS TOTAL — vai DIRETO para dashboard, sem passar por /onboarding,
      // sem renderizar VerificationStep, sem consultar /auth/onboarding-status.
      // Usamos window.location.assign para forçar re-mount do AuthProvider
      // (que vai ler o token + fetchUser e renderizar /dashboard limpo).
      setTimeout(() => {
        window.location.assign("/dashboard");
      }, 600);
    } catch (err) {
      const msg = err.response?.data?.detail || "Falha ao resgatar código.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  // ────────────────────────── render
  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Nunito', sans-serif", backgroundColor: "#F8F7FF" }}>
      <style>{`
        @keyframes vipFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .vip-anim-1 { animation: vipFadeUp 0.5s cubic-bezier(.22,.9,.32,1) both; }
        .vip-anim-2 { animation: vipFadeUp 0.5s cubic-bezier(.22,.9,.32,1) 0.08s both; }
        .vip-anim-3 { animation: vipFadeUp 0.5s cubic-bezier(.22,.9,.32,1) 0.16s both; }
        @keyframes crownPulse {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 8px rgba(245,158,11,0.45)); }
          50% { transform: scale(1.05); filter: drop-shadow(0 0 18px rgba(245,158,11,0.7)); }
        }
        .vip-crown { animation: crownPulse 2.4s ease-in-out infinite; }
      `}</style>

      <header className="px-6 sm:px-10 py-5 flex items-center justify-between bg-white/60 backdrop-blur-sm" style={{ borderBottom: "1px solid #E9E5FF" }}>
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5 group" data-testid="vip-brand-home">
          <LiventyLogoMark size={32} />
          <LiventyWordmark height={22} variant="dark" />
        </button>
        <Link to="/login" className="text-[13px] font-bold transition-colors text-zinc-600 hover:text-zinc-900" data-testid="vip-back-to-login">
          Entrar
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-10 sm:py-14 relative overflow-hidden">
        {/* Glows brand */}
        <div className="absolute pointer-events-none" style={{ top: "10%", left: "-12%", width: "min(440px,55vw)", height: "min(440px,55vw)", borderRadius: "50%", background: "radial-gradient(50% 50% at 50% 50%, rgba(245,158,11,0.22), transparent 70%)", filter: "blur(60px)" }} />
        <div className="absolute pointer-events-none" style={{ bottom: "5%", right: "-12%", width: "min(480px,55vw)", height: "min(480px,55vw)", borderRadius: "50%", background: "radial-gradient(50% 50% at 50% 50%, rgba(139,92,246,0.20), transparent 70%)", filter: "blur(60px)" }} />

        <div className="w-full max-w-[440px] relative z-10">
          {/* ── State: validating ───────────────────────────── */}
          {validating && (
            <div className="vip-anim-1 text-center py-16">
              <Loader2 size={32} className="animate-spin mx-auto text-violet-500" />
              <p className="mt-4 text-[14px] text-zinc-500" data-testid="vip-validating">Validando seu convite VIP…</p>
            </div>
          )}

          {/* ── State: invalid ───────────────────────────── */}
          {!validating && error && (
            <div className="vip-anim-1 bg-white rounded-3xl p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)] border border-rose-100" data-testid="vip-error-card">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-rose-50 flex items-center justify-center mb-5">
                <AlertTriangle size={28} className="text-rose-500" />
              </div>
              <h1 className="text-[22px] font-bold text-center text-zinc-900 tracking-tight">Convite indisponível</h1>
              <p className="text-center text-[14px] text-zinc-500 mt-2">{error}</p>
              <p className="text-center text-[12.5px] text-zinc-400 mt-4">
                Se você acha que isso é um erro, fale com quem te enviou o convite.
              </p>
              <button
                onClick={() => navigate("/get-started")}
                className="w-full mt-6 h-12 rounded-xl bg-zinc-900 text-white text-[14px] font-bold hover:bg-zinc-800 transition-colors"
                data-testid="vip-error-fallback-btn"
              >
                Criar conta normal (com trial)
              </button>
            </div>
          )}

          {/* ── State: valid → form ───────────────────────────── */}
          {!validating && !error && grant && (
            <>
              {/* Crown header */}
              <div className="vip-anim-1 text-center mb-8">
                <div className="vip-crown inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-400 via-orange-400 to-fuchsia-500 mb-5 shadow-[0_10px_40px_-10px_rgba(245,158,11,0.6)]">
                  <Crown size={36} className="text-white" strokeWidth={2.2} />
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 mb-3" data-testid="vip-badge">
                  <Sparkles size={12} className="text-amber-600" />
                  <span className="text-[11px] font-extrabold text-amber-700 uppercase tracking-widest">Convite VIP</span>
                </div>
                <h1 className="text-[28px] sm:text-[32px] tracking-tight leading-tight font-bold text-zinc-900">
                  {grant.name ? `Olá, ${grant.name.split(" ")[0]}!` : "Olá!"}
                </h1>
                <p className="text-[14px] text-zinc-500 mt-2 leading-relaxed">
                  Você ganhou acesso <span className="font-bold text-zinc-900">Liventy PRO</span> por{" "}
                  <span className="font-bold text-zinc-900">{grant.duration_days} dias</span> — totalmente gratuito.
                </p>
              </div>

              {/* Form */}
              <div className="vip-anim-2 bg-white rounded-3xl p-6 sm:p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)] border border-violet-100">
                <form onSubmit={handleRedeem} className="space-y-4" data-testid="vip-redeem-form">
                  {/* Email (locked/pre-filled) */}
                  <div>
                    <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Email autorizado</label>
                    <div className="relative">
                      <input
                        type="email"
                        value={grant.email}
                        disabled
                        readOnly
                        className="w-full h-12 px-4 pl-11 rounded-xl bg-zinc-50 border border-zinc-200 text-[14px] text-zinc-700 cursor-not-allowed font-medium"
                        data-testid="vip-email-input"
                      />
                      <CheckCircle2 size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500" />
                    </div>
                    <p className="text-[11.5px] text-zinc-400 mt-1.5">Apenas este email pode resgatar o convite (intransferível)</p>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Seu nome completo</label>
                    <div className="relative">
                      <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Como vamos te chamar?"
                        required
                        minLength={2}
                        autoComplete="name"
                        className="w-full h-12 px-4 pl-11 rounded-xl bg-white border border-zinc-200 text-[14px] text-zinc-900 focus:outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-100 transition-all"
                        data-testid="vip-name-input"
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Crie uma senha</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                      <input
                        type={showPwd ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                        required
                        minLength={6}
                        autoComplete="new-password"
                        className="w-full h-12 px-4 pl-11 pr-11 rounded-xl bg-white border border-zinc-200 text-[14px] text-zinc-900 focus:outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-100 transition-all"
                        data-testid="vip-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                        tabIndex={-1}
                        data-testid="vip-toggle-pwd"
                      >
                        {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-13 mt-4 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-amber-500 text-white text-[15px] font-bold tracking-tight flex items-center justify-center gap-2 shadow-[0_8px_24px_-8px_rgba(139,92,246,0.5)] hover:shadow-[0_12px_32px_-8px_rgba(139,92,246,0.7)] active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ height: 52 }}
                    data-testid="vip-redeem-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Liberando seu acesso…
                      </>
                    ) : (
                      <>
                        <Crown size={18} strokeWidth={2.4} />
                        Resgatar Liventy PRO
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Footer note */}
              <div className="vip-anim-3 mt-6 text-center">
                <p className="text-[11.5px] text-zinc-400">
                  Ao continuar você aceita os{" "}
                  <Link to="/termos" className="font-bold text-zinc-600 hover:text-zinc-900">Termos</Link>{" "}e a{" "}
                  <Link to="/privacidade" className="font-bold text-zinc-600 hover:text-zinc-900">Privacidade</Link>.
                </p>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
